import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import Uppy from '@uppy/core';
import { UppyContextProvider, useDropzone, useFileInput } from '@uppy/react';
import AwsS3 from '@uppy/aws-s3';

import { APIdeleteAsset, APIupdateAsset } from '../../api/Assets';
import { getFileMetadata } from '../../helpers/file';
import { getHeaderCrop, getHeaderImageStyle, moveHeaderCrop } from '../../helpers/header-image';
import type { HeaderCrop } from '../../helpers/header-image';

import type { UppyFile } from '@uppy/core';
import type { Asset } from '../../types/asset';

import '@uppy/core/css/style.css';
import '@uppy/react/css/style.css';

interface TimelineCreateHeaderImageProps {
  asset: Asset | null;
  canEdit?: boolean;
  onChange: (assetId: string | null) => void;
  setUploading: (uploading: boolean) => void;
}

interface HeaderImageDropzoneProps {
  asset: Asset | null;
  file?: UppyFile<Record<string, unknown>, Record<string, unknown>>;
  uploading: boolean;
  readOnly: boolean;
  onReplace: () => void;
  onRemove: () => void;
  onPositionChange: (crop: HeaderCrop) => Promise<void>;
}

const HeaderImageDropzone: React.FC<HeaderImageDropzoneProps> = ({ asset, file, uploading, readOnly, onReplace, onRemove, onPositionChange }) => {
  const { getRootProps } = useDropzone({ noClick: true, onDrop: onReplace });
  const { getInputProps, getButtonProps } = useFileInput();
  const inputProps = getInputProps();
  const buttonProps = getButtonProps();
  const rootProps = getRootProps();
  const [localUrl, setLocalUrl] = useState<string>();
  const [crop, setCrop] = useState(() => getHeaderCrop(asset?.metadata));
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const cropRef = useRef(crop);
  const savedCropRef = useRef(crop);
  const dragRef = useRef<{ x: number; y: number; crop: HeaderCrop } | null>(null);
  const imageUrl = localUrl || asset?.path;
  const disabled = uploading || saving;

  useEffect(() => {
    if (!file || !(file.data instanceof Blob)) {
      return;
    }
    const url = URL.createObjectURL(file.data);
    setLocalUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file?.data]);

  const updateCrop = (position: HeaderCrop) => {
    cropRef.current = position;
    setCrop(position);
  };

  const savePosition = async () => {
    const position = cropRef.current;
    if (savingRef.current || (position.x === savedCropRef.current.x && position.y === savedCropRef.current.y
      && position.zoom === savedCropRef.current.zoom)) {
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      await onPositionChange(position);
      savedCropRef.current = position;
    } catch {
      updateCrop(savedCropRef.current);
      toast.error('Failed to save image position. Please try again.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const image = imageUrl && (
    <img
      ref={imageRef}
      className="timeline-header-image-preview"
      src={imageUrl}
      alt=""
      draggable={false}
      style={getHeaderImageStyle(crop)}
    />
  );

  const handleImageClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (imageUrl || (event.target instanceof Element && event.target.closest('wa-button'))) {
      return;
    }
    buttonProps.onClick();
  };

  if (readOnly) {
    return (
      <div className="timeline-header-image">
        <div
          className={`timeline-header-image-dropzone is-read-only${imageUrl ? ' has-image' : ''}`}
        >
          {image}
          {!imageUrl && <i className="fa-light fa-image fa-2x" />}
        </div>
      </div>
    );
  }

  return (
    <div className="timeline-header-image-uploader">
      <input
        {...inputProps}
        className="wa-visually-hidden"
        disabled={disabled}
        onChange={(event) => {
          if (event.currentTarget.files?.length) {
            onReplace();
          }
          inputProps.onChange(event as any);
        }}
      />
      <div
        {...rootProps}
        onDropCapture={(event) => {
          if (disabled) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        onClick={handleImageClick}
        className={`timeline-header-image-dropzone${imageUrl ? ' has-image' : ''}`}
        tabIndex={imageUrl && !disabled ? 0 : undefined}
        role={imageUrl ? 'group' : undefined}
        aria-label={imageUrl ? 'Header image position. Drag or use arrow keys to reposition.' : undefined}
        aria-busy={disabled}
        onPointerDown={(event) => {
          if (disabled || !imageRef.current?.naturalWidth || event.button !== 0) {
            return;
          }
          event.preventDefault();
          event.currentTarget.focus();
          event.currentTarget.setPointerCapture(event.pointerId);
          dragRef.current = { x: event.clientX, y: event.clientY, crop: cropRef.current };
        }}
        onPointerMove={(event) => {
          const drag = dragRef.current;
          const img = imageRef.current;
          if (!drag || !img) {
            return;
          }
          const { width, height } = event.currentTarget.getBoundingClientRect();
          updateCrop(moveHeaderCrop(drag.crop, event.clientX - drag.x, event.clientY - drag.y,
            width, height, img.naturalWidth, img.naturalHeight));
        }}
        onPointerUp={(event) => {
          if (dragRef.current) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
        }}
        onLostPointerCapture={() => {
          if (dragRef.current) {
            dragRef.current = null;
            void savePosition();
          }
        }}
        onKeyDown={(event) => {
          const img = imageRef.current;
          if (event.target !== event.currentTarget || disabled || !img?.naturalWidth || !event.key.startsWith('Arrow')) {
            return;
          }
          event.preventDefault();
          const step = event.shiftKey ? 50 : 10;
          const { width, height } = event.currentTarget.getBoundingClientRect();
          updateCrop(moveHeaderCrop(cropRef.current,
            event.key === 'ArrowRight' ? step : event.key === 'ArrowLeft' ? -step : 0,
            event.key === 'ArrowDown' ? step : event.key === 'ArrowUp' ? -step : 0,
            width, height, img.naturalWidth, img.naturalHeight));
        }}
        onKeyUp={(event) => {
          if (event.target === event.currentTarget && event.key.startsWith('Arrow')) {
            void savePosition();
          }
        }}
        onBlur={() => { void savePosition(); }}
      >
        {image}
        {!imageUrl && (
          <i className="fa-light fa-image fa-2x" />
        )}
      </div>
      {(imageUrl && !uploading) && (
        <small>{saving ? 'Saving image position...' : 'Zoom in to move the image in any direction. Drag to reposition. Changes are saved automatically.'}</small>
      )}
      {imageUrl && (
        <label className="timeline-header-image-zoom">
          <span>Zoom</span>
          <input
            type="range"
            min={100}
            max={300}
            step={5}
            value={Math.round((crop.zoom ?? 1) * 100)}
            aria-label="Header image zoom"
            aria-valuetext={`${Math.round((crop.zoom ?? 1) * 100)}%`}
            disabled={disabled}
            onChange={(event) => updateCrop({ ...cropRef.current, zoom: Number(event.currentTarget.value) / 100 })}
            onPointerUp={() => { void savePosition(); }}
            onPointerCancel={() => { void savePosition(); }}
            onKeyUp={() => { void savePosition(); }}
            onBlur={() => { void savePosition(); }}
          />
          <span>{Math.round((crop.zoom ?? 1) * 100)}%</span>
        </label>
      )}
      <div className="timeline-header-image-actions">
        <wa-button {...buttonProps} disabled={disabled}>
          {uploading ? (
            <><wa-spinner slot="start" /> Uploading...</>
          ) : (
            <><i className="fa-light fa-arrow-up-from-bracket" /> {asset || file ? 'Replace image' : 'Add header image'}</>
          )}
        </wa-button>
        {(asset || file) && (
          <wa-button
            type="button"
            appearance="plain"
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
          >
            Remove
          </wa-button>
        )}
      </div>
    </div>
  );
};

const TimelineCreateHeaderImage: React.FC<TimelineCreateHeaderImageProps> = ({ asset, canEdit = true, onChange, setUploading }) => {
  const [files, setFiles] = useState<UppyFile<Record<string, unknown>, Record<string, unknown>>[]>([]);
  const [uploading, setLocalUploading] = useState(false);

  const [uppy] = useState(() => new Uppy({
    autoProceed: true,
    restrictions: {
      allowedFileTypes: ['.jpg', '.jpeg', '.png'],
      maxNumberOfFiles: 1,
    },
  })
    .on('file-added', () => {
      setFiles(uppy.getFiles());
    })
    .on('file-removed', (file) => {
      if (file.meta.uuid) {
        APIdeleteAsset(file.meta.uuid as string);
      }
      onChange(null);
      setFiles(uppy.getFiles());
    })
    .on('upload', () => {
      setLocalUploading(true);
      setUploading(true);
    })
    .on('upload-progress', () => {
      setFiles(uppy.getFiles());
    })
    .on('upload-success', async (file) => {
      try {
        if (file) {
          const metadata = await getFileMetadata(file.data as File);
          await APIupdateAsset(file.meta.uuid as string, { status: 'uploaded', metadata });
          onChange(file.meta.uuid as string);
        }
      } catch {
        toast.error('Failed to finish uploading the header image. Please replace it and try again.');
      } finally {
        setFiles(uppy.getFiles());
        setLocalUploading(false);
        setUploading(false);
      }
    })
    .on('upload-error', () => {
      setFiles(uppy.getFiles());
      setLocalUploading(false);
      setUploading(false);
    })
    .use(AwsS3, {
      shouldUseMultipart: false,
      getUploadParameters: async (file) => window.axios('/api/assets/upload', {
        method: 'post',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
        data: JSON.stringify({
          filename: file.name,
          contentType: file.type,
          size: file.size,
        }),
      }).then((response) => response.data).then((data) => {
        uppy.setFileMeta(file.id, { ...data.fields });
        return {
          method: data.method,
          url: data.url,
          fields: data.fields,
          headers: data.headers,
        };
      }),
    }));

  const handleRemove = () => {
    const currentFile = uppy.getFiles()[0];
    if (currentFile) {
      uppy.removeFile(currentFile.id);
      return;
    }
    if (asset) {
      APIdeleteAsset(asset.id);
    }
    onChange(null);
  };

  const handleReplace = () => {
    const currentFile = uppy.getFiles()[0];
    if (currentFile) {
      uppy.removeFile(currentFile.id);
    } else if (asset) {
      APIdeleteAsset(asset.id);
      onChange(null);
    }
  };

  return (
    <section className="timeline-header-image">
      {canEdit && (
        <div className="page-eyebrow">
          Timeline header image
        </div>
      )}
      <UppyContextProvider uppy={uppy}>
        <HeaderImageDropzone
          key={files[0]?.id ?? asset?.id ?? 'empty'}
          asset={asset}
          file={files[0]}
          uploading={uploading}
          readOnly={!canEdit}
          onReplace={handleReplace}
          onRemove={handleRemove}
          onPositionChange={async (crop) => {
            const assetId = files[0]?.meta.uuid ?? asset?.id;
            if (typeof assetId !== 'string') {
              throw new Error('Header image has not finished uploading.');
            }
            setUploading(true);
            try {
              await APIupdateAsset(assetId, { metadata: { header_crop: crop } });
            } finally {
              setUploading(false);
            }
          }}
        />
      </UppyContextProvider>
    </section>
  );
};

export default TimelineCreateHeaderImage;
